import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { ErrorAlert } from '../components/ui'

export default function SetupPassword() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const [verified, setVerified] = useState(false)
  const [clientName, setClientName] = useState('')
  const verifyAttempted = useRef(false)

  useEffect(() => {
    // Guard: verifyOtp consumes the one-time token on first call
    if (verifyAttempted.current) return
    verifyAttempted.current = true

    const applySession = (user) => {
      setVerified(true)
      if (user?.user_metadata?.full_name) {
        setClientName(user.user_metadata.full_name)
      }
      setVerifying(false)
    }

    const verifyToken = async () => {
      const tokenHash = searchParams.get('token_hash')
      const type = searchParams.get('type')

      try {
        // Step 1: Check for existing session first.
        // This handles re-visits where the session is still in localStorage
        // (e.g., user clicked link once, token consumed, then navigated back).
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          applySession(session.user)
          return
        }

        // Step 2: Client-side token_hash verification via verifyOtp (POST).
        // Uses POST request which is immune to email scanner prefetching.
        if (tokenHash && type) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type,
          })

          if (!error) {
            const { data: { user } } = await supabase.auth.getUser()
            applySession(user)
            return
          }

          // Token was already consumed — check if session exists now
          const { data: { session: retrySession } } = await supabase.auth.getSession()
          if (retrySession) {
            applySession(retrySession.user)
            return
          }

          setError(t('setupPassword.expiredLink'))
          setVerifying(false)
          return
        }

        // No session and no token_hash — invalid link
        setError(t('setupPassword.invalidLink'))
      } catch (err) {
        setError(err.message)
      } finally {
        setVerifying(false)
      }
    }

    verifyToken()
  }, [searchParams, t])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (password.length < 6) {
      setError(t('setupPassword.passwordMinLength'))
      return
    }

    if (password !== confirmPassword) {
      setError(t('setupPassword.passwordMismatch'))
      return
    }

    setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error

      // Update client_pairs password_set_at via the client's session
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        await supabase
          .from('client_pairs')
          .update({ male_password_set_at: new Date().toISOString() })
          .eq('male_client_id', user.id)
          .is('male_password_set_at', null)

        await supabase
          .from('client_pairs')
          .update({ female_password_set_at: new Date().toISOString() })
          .eq('female_client_id', user.id)
          .is('female_password_set_at', null)
      }

      navigate('/client', { replace: true })
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500 text-lg">{t('setupPassword.verifying')}</div>
      </div>
    )
  }

  if (!verified) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        <div className="w-full max-w-md text-center animate-fadeIn">
          <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl">
            &#10007;
          </div>
          <h1 className="font-display text-2xl font-medium text-gray-800 mb-3">
            {t('setupPassword.invalidInvitationTitle')}
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/login" className="text-primary-600 font-semibold hover:underline">
            {t('setupPassword.goToLogin')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md animate-fadeIn">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-medium text-gray-800 mb-2">
            {t('setupPassword.welcomeTitle', { name: clientName ? `, ${clientName}` : '' })}
          </h1>
          <p className="text-gray-500">
            {t('setupPassword.welcomeSubtitle')}
          </p>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-sm">
          <ErrorAlert message={error} onDismiss={() => setError(null)} />

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('setupPassword.passwordLabel')}</label>
              <input
                type="password"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('setupPassword.passwordPlaceholder')}
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('setupPassword.confirmPasswordLabel')}</label>
              <input
                type="password"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('setupPassword.confirmPasswordPlaceholder')}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? t('setupPassword.settingUp') : t('setupPassword.setPasswordButton')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
